import { Fragment, useEffect, useState, useContext } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Row, Col, Tab, Nav, Card } from 'react-bootstrap';
import Axios from 'axios';
import { Formik } from 'formik';
import {
  CRUDLayout,
  Alert,
  DataStatus,
  BackButton,
  ActionButton,
  InfoItemHorizontal,
} from 'components';
import { PermintaanUpahProduksiApi } from 'api';
import {
  InfoDetailJobOrder,
  ModalFormKontrak,
  ModalFormRegistrasi,
  TableItemKontrak,
  TablePermintaan,
} from './Components';
import {
  TransferUpahProduksiContext,
  withDropdownTransferUpahContextProvider,
  withTransferUpahProduksiProvider,
} from './Context';
import { useMockNumber } from './Hooks';
import { DateConvert } from 'utilities';

const NavTab = ({ eventKey, title }) => (
  <Nav.Item>
    <Nav.Link eventKey={eventKey}>{title}</Nav.Link>
  </Nav.Item>
);

const TabPane = ({ eventKey, component }) => (
  <Tab.Pane eventKey={eventKey}>{component}</Tab.Pane>
);

const DetailTransferUpahProduksi = ({ setNavbarTitle }) => {
  const history = useHistory();
  const { id_permintaan_produksi, id_kontrak_upah } = useParams();
  const [generateMockNumber] = useMockNumber();
  const {
    dataTransfer,
    dataJobOrder,
    setJobOrder,
    modalForm,
    dataPermintaan,
    setDataPermintaan,
    setModalForm,
  } = useContext(TransferUpahProduksiContext);

  const [dataKontrak, setDataKontrak] = useState({});
  const [isPageLoading, setPageLoading] = useState(true);
  const [alert, setAlert] = useState({
    variant: 'primary',
    text: '',
  });
  const isLC = dataKontrak?.ref_kontrak_upah === 'klc';
  const title = isLC ? 'Labour Cost' : 'Labour Cost Finishing';

  const getInitialData = () => {
    setPageLoading(true);

    Axios.all([
      PermintaanUpahProduksiApi.single({ id_permintaan_produksi }),
      PermintaanUpahProduksiApi.getSingleKontrakUpah({ id_kontrak_upah }),
    ])
      .then(
        Axios.spread((resPermintaan, resKontrakUpah) => {
          const { labour_cost, labour_cost_finishing, history, ...jobOrder } =
            resPermintaan?.data?.data;

          setJobOrder(jobOrder ?? {});
          setDataKontrak(resKontrakUpah?.data?.data ?? {});
          setDataPermintaan({
            lc: labour_cost?.map((item) => ({
              ...item,
              status_permintaan: item.status_permintaan ?? 'waiting',
            })),
            lcf: labour_cost_finishing?.map((item) => ({
              ...item,
              status_permintaan: item.status_permintaan ?? 'waiting',
            })),
          });
        })
      )
      .catch(() => setAlert({ text: 'Data gagal dimuat', variant: 'danger' }))
      .finally(() => setPageLoading(false));
  };

  useEffect(() => {
    setNavbarTitle('Transfer Upah Produksi');
    getInitialData();
  }, []);

  const showModalKontrak = () =>
    setModalForm((prev) => ({
      ...prev,
      action: 'create',
      tab: isLC ? 'lc' : 'lcf',
      showKontrak: true,
      data: dataKontrak,
    }));

  return (
    <CRUDLayout>
      <Row className="d-flex align-items-end justify-content-between mb-2">
        <Col>
          <div>Detail Data Job Order </div>
        </Col>
        <Col className="d-flex justify-content-end align-items-end">
          <BackButton onClick={() => history.goBack()} />
        </Col>
      </Row>

      <Alert
        showCloseButton
        className="mt-2"
        show={!!alert?.text?.length}
        variant={alert.variant}
        text={alert.text}
        onClose={() => setAlert((prev) => ({ ...prev, text: '' }))}
      />

      {isPageLoading ? (
        <DataStatus loading={true} text="Memuat data . . ." />
      ) : (
        <Fragment>
          <InfoDetailJobOrder data={dataJobOrder} />

          <Card className="mt-4">
            <Tab.Container defaultActiveKey="detail">
              {/* Tab Navigation */}
              <Card.Header>
                <Nav variant="tabs" defaultActiveKey="detail">
                  <NavTab eventKey="detail" title={title} />
                </Nav>
              </Card.Header>

              {/* Tab Content */}
              <Card.Body>
                <Tab.Content>
                  <TabPane
                    eventKey="detail"
                    component={
                      <Fragment>
                        <Row className="d-flex justify-content-between align-items-end mb-2">
                          <Col>
                            <div
                              className="font-weight-bold"
                              style={{ fontSize: '.9rem' }}
                            >
                              List Data Permintaan {title}
                            </div>
                          </Col>
                          <Col className="d-flex justify-content-end align-items-end">
                            <ActionButton
                              size="sm"
                              text="Lihat Kontrak"
                              onClick={showModalKontrak}
                            />
                          </Col>
                        </Row>

                        <TablePermintaan
                          tab={isLC ? 'lc' : 'lcf'}
                          tipe="readonly"
                          data={
                            isLC ? dataPermintaan.lc : dataPermintaan.lcf ?? []
                          }
                        />

                        <div
                          className="font-weight-bold mt-4"
                          style={{ fontSize: '.9rem' }}
                        >
                          List Data Transfer {title}
                        </div>

                        <InfoItemHorizontal
                          className="mt-1"
                          label="Tgl. Transfer Upah Produksi"
                          text={
                            dataKontrak.tgl_kontrak_upah
                              ? DateConvert(
                                  new Date(dataKontrak.tgl_kontrak_upah)
                                ).detail
                              : '-'
                          }
                        />

                        <InfoItemHorizontal
                          label="No. Transfer Upah Produksi"
                          text={dataKontrak.no_kontrak_upah ?? '-'}
                        />

                        <InfoItemHorizontal
                          label="Vendor"
                          text={dataKontrak.nama_vendor ?? '-'}
                          className="mb-2"
                        />

                        <Formik initialValues={dataKontrak}>
                          {() => <TableItemKontrak readOnly />}
                        </Formik>
                      </Fragment>
                    }
                  />
                </Tab.Content>
              </Card.Body>
            </Tab.Container>
          </Card>
        </Fragment>
      )}

      {modalForm.showRegistrasi && (
        <ModalFormRegistrasi generateMockNumber={generateMockNumber} />
      )}

      {modalForm.showKontrak && <ModalFormKontrak readOnly />}
    </CRUDLayout>
  );
};

export default withDropdownTransferUpahContextProvider(
  withTransferUpahProduksiProvider(DetailTransferUpahProduksi)
);
